import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, ArrowRight, FileText, BookOpen, Check, Palette } from "lucide-react";
import Footer from "@/components/Footer";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n));

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const Pricing = () => {
  const [selectedFormat, setSelectedFormat] = useState("digital");
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API}/studio/pricing`);
        const tierData = Object.values(res.data.tiers).sort((a, b) => a.pages - b.pages);
        setTiers(tierData);
      } catch {
        // Fallback
        setTiers([
          { pages: 25, digital: 399, physical_softcover: 1249, physical_hardcover: 1999, color_markup: 399 },
          { pages: 50, digital: 649, physical_softcover: 1899, physical_hardcover: 2899, color_markup: 649 },
          { pages: 100, digital: 999, physical_softcover: 2899, physical_hardcover: 3999, color_markup: 999 },
          { pages: 150, digital: 1399, physical_softcover: 3699, physical_hardcover: 5499, color_markup: 1249 },
        ]);
      }
    };
    fetchPricing();
  }, []);

  const formats = [
    { id: "digital", label: "Digital PDF", icon: FileText, desc: "Free during launch!", enabled: true },
    { id: "softcover", label: "Softcover Print", icon: BookOpen, desc: "Coming soon", enabled: false },
    { id: "hardcover", label: "Hardcover Print", icon: BookOpen, desc: "Coming soon", enabled: false },
  ];

  const features = {
    digital: [
      "High-resolution magazine-style PDF",
      "Cover page with custom fonts & colors",
      "All chapters beautifully formatted",
      "Photos embedded at full quality",
      "Instant download after payment",
      "Shareable digital file",
    ],
    softcover: [
      "Everything in Digital, plus:",
      "Professional softcover print",
      "Glossy or matte cover finish",
      "Delivered to your doorstep",
      "Optional color interior pages",
      "Perfect for gifting",
    ],
    hardcover: [
      "Everything in Softcover, plus:",
      "Premium hardcover binding",
      "Dust jacket included",
      "Archival-quality paper",
      "Lays flat when open",
      "The ultimate keepsake",
    ],
  };

  return (
    <div className="min-h-screen bg-[#F8F6F1] flex flex-col" data-testid="pricing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#F8F6F1]/80 backdrop-blur-xl border-b border-[#D4C9B8]/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center">
              <Leaf className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif text-xl font-medium text-[#2D3A2D]">My Story</span>
          </Link>
          <Link to="/login" className="rounded-full px-6 py-2 bg-[#4A5D4A] text-[#F8F6F1] hover:bg-[#2D3A2D] transition-colors font-sans text-sm font-medium" data-testid="pricing-cta-nav">
            Start Your Story
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <main className="flex-1">
        <section className="max-w-5xl mx-auto px-6 md:px-12 pt-16 pb-10 text-center">
          <motion.p initial="hidden" animate="visible" variants={fadeUp} className="font-sans text-sm uppercase tracking-[0.2em] text-[#7A9B7E] mb-4">
            Pricing
          </motion.p>
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp} className="font-serif text-4xl sm:text-5xl font-medium text-[#2D3A2D] mb-4 tracking-tight" data-testid="pricing-title">
            Turn Memories into a Book
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} className="font-sans text-base sm:text-lg text-[#4A5D4A] max-w-2xl mx-auto mb-3">
            AI conversations, story creation, sharing & PDF export are <strong className="text-[#2D3A2D]">free during our launch offer</strong>. Physical prints coming soon!
          </motion.p>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} className="font-sans text-sm text-[#7A8A7A]">
            All prices in Indian Rupees (₹). Inclusive of taxes.
          </motion.p>
        </section>

        {/* Format Toggle */}
        <section className="max-w-5xl mx-auto px-6 md:px-12 mb-10">
          <div className="flex justify-center">
            <div className="inline-flex bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-1.5 gap-1" data-testid="format-toggle">
              {formats.map((f) => (
                <button
                  key={f.id}
                  onClick={() => f.enabled && setSelectedFormat(f.id)}
                  disabled={!f.enabled}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl font-sans text-sm transition-all ${
                    !f.enabled
                      ? "text-[#ccc] cursor-not-allowed opacity-50"
                      : selectedFormat === f.id
                      ? "bg-[#4A5D4A] text-white shadow-md"
                      : "text-[#7A8A7A] hover:bg-[#F8F6F1]"
                  }`}
                  data-testid={`format-btn-${f.id}`}
                >
                  <f.icon className="w-4 h-4" />
                  {f.label}
                  {!f.enabled && <span className="text-[9px] ml-1 opacity-60">Soon</span>}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Grid */}
        <section className="max-w-5xl mx-auto px-6 md:px-12 mb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pricing-grid">
            {tiers.map((tier, i) => {
              const price = selectedFormat === "digital" ? tier.digital
                : selectedFormat === "softcover" ? tier.physical_softcover : tier.physical_hardcover;
              const isPopular = i === 1;
              return (
                <motion.div
                  key={tier.pages}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative bg-white/80 backdrop-blur-sm border rounded-2xl p-6 flex flex-col ${
                    isPopular ? "border-[#7A9B7E] shadow-lg ring-1 ring-[#7A9B7E]/20" : "border-[#D4C9B8]/40"
                  }`}
                  data-testid={`pricing-tier-${tier.pages}`}
                >
                  {isPopular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#7A9B7E] text-white text-[10px] font-sans font-medium px-3 py-1 rounded-full uppercase tracking-wider">
                      Most Popular
                    </span>
                  )}
                  <p className="font-serif text-3xl font-medium text-[#2D3A2D] mb-1">{tier.pages}</p>
                  <p className="font-sans text-xs text-[#7A8A7A] mb-5">pages</p>

                  <div className="mb-5">
                    <span className="font-serif text-3xl font-medium text-[#2D3A2D]">₹{formatINR(price)}</span>
                  </div>

                  {selectedFormat !== "digital" && (
                    <div className="flex items-center gap-1.5 mb-5 text-[#7A8A7A]">
                      <Palette className="w-3.5 h-3.5" />
                      <span className="font-sans text-xs">Color pages: +₹{formatINR(tier.color_markup)}</span>
                    </div>
                  )}

                  <Link
                    to="/login"
                    className={`w-full py-3 rounded-xl font-sans text-sm font-medium text-center transition-all block ${
                      isPopular
                        ? "bg-[#4A5D4A] text-white hover:bg-[#2D3A2D]"
                        : "bg-[#F8F6F1] text-[#4A5D4A] border border-[#D4C9B8] hover:bg-[#E5D4C1]/40"
                    }`}
                    data-testid={`pricing-cta-${tier.pages}`}
                  >
                    Get Started
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Feature list */}
        <section className="max-w-3xl mx-auto px-6 md:px-12 mb-20">
          <div className="bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-8" data-testid="pricing-features">
            <h3 className="font-serif text-xl font-medium text-[#2D3A2D] mb-5">
              What's included in {formats.find(f => f.id === selectedFormat)?.label}
            </h3>
            <ul className="space-y-3">
              {features[selectedFormat].map((feat, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-[#7A9B7E] mt-0.5 flex-shrink-0" />
                  <span className="font-sans text-sm text-[#4A5D4A]">{feat}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Free CTA */}
        <section className="max-w-3xl mx-auto px-6 md:px-12 mb-20 text-center">
          <div className="bg-[#9FB89F]/10 border border-[#9FB89F]/20 rounded-2xl p-10">
            <h3 className="font-serif text-2xl font-medium text-[#2D3A2D] mb-3">
              Start for free. Pay when you're ready.
            </h3>
            <p className="font-sans text-sm text-[#7A8A7A] mb-6 max-w-md mx-auto">
              Create your memoir with unlimited AI conversations, voice input, and story generation — completely free. Export or print whenever you choose.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#4A5D4A] text-[#F8F6F1] hover:bg-[#2D3A2D] transition-all font-sans font-medium text-sm shadow-lg"
              data-testid="pricing-start-free"
            >
              Start Your Story — It's Free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
